import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Brain, Download, RefreshCw, CheckCircle, Save } from 'lucide-react';
import NeuroInformedBadge from '../NeuroInformedBadge';
import HelperIcon from '../HelperIcon';
import { helperContent } from '../../content/helper-content';
import { apiAIFetch, apiFetch } from '../../api';
import '../../styles/whiteboard-session.css';

/**
 * MetacognitiveWrapup Component
 *
 * End-of-session reflection panel that:
 * - Prompts metacognitive reflection questions
 * - Generates AI summary of the session
 * - Allows export of summary
 *
 * Uses the Metacognition neuro-technique to build self-regulated learning.
 */

const REFLECTION_PROMPTS = [
  {
    id: 'strategy',
    question: "What strategy helped you most today?",
    placeholder: "e.g., Drawing diagrams, breaking into steps..."
  },
  {
    id: 'remember',
    question: "What's one thing you'll remember from today?",
    placeholder: "e.g., The relationship between..."
  },
  {
    id: 'next',
    question: "What would you like to explore next?",
    placeholder: "e.g., More practice with..., or learn about..."
  }
];

const MetacognitiveWrapup = ({
  isOpen,
  onClose,
  sessionContext,
  whiteboardSnapshot,
  onExport,
  studentContext,
  whiteboardTitle
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [responses, setResponses] = useState({});
  const [summary, setSummary] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [isSavingSession, setIsSavingSession] = useState(false);
  const [sessionSaved, setSessionSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Reset state when panel opens
  useEffect(() => {
    if (isOpen) {
      setResponses({});
      setSummary(null);
      setSummaryError(null);
      setSessionSaved(false);
      setSaveError(null);
    }
  }, [isOpen]);

  // Save session to backend
  const saveSession = async () => {
    setIsSavingSession(true);
    setSaveError(null);

    try {
      const response = await apiFetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_uuid: studentContext?.uuid || null,
          title: whiteboardTitle || 'Whiteboard Session',
          whiteboard_snapshot: whiteboardSnapshot || null,
          phase_completion: {
            opening: true,
            core: true,
            closing: true
          },
          reflections: responses,
          ai_summary: summary || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save session');
      }

      await response.json();
      setSessionSaved(true);

      // Show success briefly then navigate to sessions
      setTimeout(() => {
        onClose();
        const nextSessionsPath = location.pathname.startsWith('/student/')
          ? '/student/sessions'
          : '/teacher/sessions';
        navigate(nextSessionsPath);
      }, 1500);
    } catch (err) {
      console.error('Error saving session:', err);
      setSaveError('Could not save session. Please try again.');
    } finally {
      setIsSavingSession(false);
    }
  };

  const handleResponseChange = (id, value) => {
    setResponses(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const generateSummary = async () => {
    setIsGeneratingSummary(true);
    setSummaryError(null);

    try {
      const response = await apiAIFetch('/api/whiteboard/generate-summary', 'whiteboard.generate_summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: sessionContext ? {
            sessionId: sessionContext.id,
            summary: sessionContext.summary || sessionContext
          } : null,
          reflections: responses,
          snapshotInfo: whiteboardSnapshot ? {
            elementCount: whiteboardSnapshot.elements?.length || 0,
            hasNotes: whiteboardSnapshot.elements?.some(e => e.type === 'NOTE'),
            hasMath: whiteboardSnapshot.elements?.some(e => e.type === 'MATH'),
            hasGraphs: whiteboardSnapshot.elements?.some(e => e.type === 'CHART')
          } : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate summary');
      }

      const data = await response.json();
      setSummary(data.summary);
    } catch (err) {
      console.error('Error generating summary:', err);
      setSummaryError('Could not generate summary. Please try again.');
      // Generate a basic summary as fallback
      setSummary(generateFallbackSummary());
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const generateFallbackSummary = () => {
    const parts = ["Session Summary\n"];

    if (responses.strategy) {
      parts.push(`Strategy that worked: ${responses.strategy}`);
    }
    if (responses.remember) {
      parts.push(`Key takeaway: ${responses.remember}`);
    }
    if (responses.next) {
      parts.push(`Next steps: ${responses.next}`);
    }

    if (parts.length === 1) {
      parts.push("Great work today! You're building new neural pathways with every session.");
    }

    return parts.join('\n\n');
  };

  const handleExport = (format) => {
    if (onExport) {
      onExport(format, {
        summary,
        reflections: responses
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="metacognitive-wrapup" onClick={onClose}>
      <div className="metacognitive-wrapup__modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="metacognitive-wrapup__header">
          <h2 className="metacognitive-wrapup__title">
            <Brain size={24} />
            Session Wrap-up
          </h2>
          <button
            className="metacognitive-wrapup__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="metacognitive-wrapup__content">
          {/* Reflection Section */}
          <div className="metacognitive-wrapup__section">
            <h3 className="metacognitive-wrapup__section-title">
              <CheckCircle size={18} />
              Reflection
              <HelperIcon
                title={helperContent.closingPhase.title}
                content={helperContent.closingPhase.content}
                size="small"
              />
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
              Take a moment to reflect on your learning. This builds metacognition -
              the ability to think about your own thinking.
            </p>
            <div className="metacognitive-wrapup__prompts">
              {REFLECTION_PROMPTS.map((prompt) => (
                <div key={prompt.id} className="metacognitive-wrapup__prompt">
                  <label className="metacognitive-wrapup__prompt-question">
                    {prompt.question}
                  </label>
                  <textarea
                    className="metacognitive-wrapup__prompt-input"
                    placeholder={prompt.placeholder}
                    value={responses[prompt.id] || ''}
                    onChange={(e) => handleResponseChange(prompt.id, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Summary Section */}
          <div className="metacognitive-wrapup__section">
            <h3 className="metacognitive-wrapup__section-title">
              <Brain size={18} />
              Session Summary
            </h3>

            {!summary && !isGeneratingSummary && (
              <button
                className="metacognitive-wrapup__btn metacognitive-wrapup__btn--primary"
                onClick={generateSummary}
                style={{ width: '100%' }}
              >
                <RefreshCw size={16} style={{ marginRight: '8px' }} />
                Generate AI Summary
              </button>
            )}

            {isGeneratingSummary && (
              <div className="session-opener-panel__loading" style={{ padding: '20px' }}>
                <div className="session-opener-panel__loading-spinner" />
                <p>Generating summary...</p>
              </div>
            )}

            {summary && (
              <div className="metacognitive-wrapup__summary">
                <div className="metacognitive-wrapup__summary-header">
                  <span className="metacognitive-wrapup__summary-title">
                    Your Session Summary
                  </span>
                  <NeuroInformedBadge
                    technique="Metacognition"
                    size="small"
                    variant="subtle"
                  />
                </div>
                <p className="metacognitive-wrapup__summary-text">
                  {summary}
                </p>
              </div>
            )}

            {summaryError && (
              <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>
                {summaryError}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="metacognitive-wrapup__actions">
            <button
              className="metacognitive-wrapup__btn metacognitive-wrapup__btn--secondary"
              onClick={onClose}
            >
              Close
            </button>
            {summary && (
              <button
                className="metacognitive-wrapup__btn metacognitive-wrapup__btn--secondary"
                onClick={() => handleExport('pdf')}
              >
                <Download size={16} style={{ marginRight: '8px' }} />
                Export
              </button>
            )}
            <button
              className="metacognitive-wrapup__btn metacognitive-wrapup__btn--primary"
              onClick={saveSession}
              disabled={isSavingSession || sessionSaved}
            >
              {sessionSaved ? (
                <>
                  <CheckCircle size={16} style={{ marginRight: '8px' }} />
                  Session Saved!
                </>
              ) : isSavingSession ? (
                'Saving...'
              ) : (
                <>
                  <Save size={16} style={{ marginRight: '8px' }} />
                  Save Session
                </>
              )}
            </button>
          </div>
          {saveError && (
            <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px', textAlign: 'center' }}>
              {saveError}
            </p>
          )}

          {/* Growth message */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '8px',
            textAlign: 'center',
            fontSize: '13px',
            color: '#92400e'
          }}>
            <strong>You've built new neural pathways today!</strong>
            <br />
            Every session strengthens your brain's learning networks.
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetacognitiveWrapup;
